import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiClientError } from "../../../../../lib/api";
import { useIdea, useWithdrawIdea } from "../../../../../lib/api/ideas";
import { getCurrentUser } from "../../../../../lib/auth";
import { useFamChannel } from "../../../../../lib/realtime";
import { ConsensusBar } from "../../../../../components/ConsensusBar";
import { RoleBadge } from "../../../../../components/RoleBadge";
import { VoteButtons } from "../../../../../components/VoteButtons";

// /fam/:slug/idea/:id — single-idea detail page. Live consensus tally
// (5s polling via useIdea), proposer details, vote buttons, withdraw button
// for the proposer. Read-only fallbacks when the idea has resolved.
export function IdeaDetailRoute() {
	const { slug, id } = useParams<{ slug: string; id: string }>();
	const ideaQuery = useIdea(id);
	const withdraw = useWithdrawIdea(slug ?? "", id ?? "");
	const [currentUserId, setCurrentUserId] = useState<string | null>(null);

	// Live updates from the FamChannel WebSocket. Shared with FamViewerRoute
	// via reference counting in lib/realtime.ts, so navigating between the
	// two doesn't churn the connection.
	useFamChannel(slug);

	useEffect(() => {
		let cancelled = false;
		void getCurrentUser().then((u) => {
			if (!cancelled) setCurrentUserId(u?.id ?? null);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	if (!slug || !id) {
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center text-sm text-[var(--color-danger)]">
				Missing fam or idea id.
			</div>
		);
	}

	if (ideaQuery.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center text-[var(--color-muted)]">
				Loading idea…
			</div>
		);
	}

	if (ideaQuery.isError || !ideaQuery.data) {
		const message =
			ideaQuery.error instanceof ApiClientError
				? ideaQuery.error.message
				: ideaQuery.error instanceof Error
					? ideaQuery.error.message
					: "";
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center">
				<p className="text-sm text-[var(--color-danger)]">
					Could not load idea. {message}
				</p>
				<Link
					to={`/fam/${slug}`}
					className="mt-4 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					Back to Fam
				</Link>
			</div>
		);
	}

	const { idea, consensus } = ideaQuery.data;
	const isResolved = idea.status !== "open";
	const isProposer = currentUserId !== null && currentUserId === idea.proposerUserId;
	const resolvesAt = new Date(idea.resolvesAt);
	const now = new Date();
	const isWindowExpired = Number.isFinite(resolvesAt.getTime())
		? resolvesAt.getTime() <= now.getTime()
		: false;
	const votingDisabled = isResolved || isWindowExpired;

	const disabledReason = (() => {
		if (idea.status === "withdrawn") return "Idea withdrawn by proposer.";
		if (idea.status === "passed") return "Idea passed and is now in the composite.";
		if (idea.status === "failed") return "Idea failed to clear the threshold.";
		if (idea.status === "quorum_failed") return "Idea closed without quorum.";
		if (idea.status === "expired") return "Voting window closed.";
		if (idea.status === "pm_unilateral")
			return "PM unilateral entry — no vote required.";
		if (isWindowExpired) return "Voting window closed.";
		return undefined;
	})();

	const windowRemaining = (() => {
		if (votingDisabled) return null;
		const diffMs = resolvesAt.getTime() - now.getTime();
		if (diffMs <= 0) return null;
		const hours = Math.floor(diffMs / (1000 * 60 * 60));
		if (hours >= 24) {
			const days = Math.floor(hours / 24);
			return `${days}d ${hours % 24}h left`;
		}
		if (hours >= 1) {
			const mins = Math.floor((diffMs / (1000 * 60)) % 60);
			return `${hours}h ${mins}m left`;
		}
		const mins = Math.max(1, Math.floor(diffMs / (1000 * 60)));
		return `${mins}m left`;
	})();

	const withdrawError = withdraw.error?.message ?? null;

	return (
		<div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
			<header>
				<Link
					to={`/fam/${slug}`}
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Back to Fam
				</Link>
				<div className="mt-2 flex flex-wrap items-center gap-3">
					<h1 className="text-3xl font-bold font-mono">{idea.symbol}</h1>
					<span
						className={`rounded-md border px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${
							idea.direction === "long"
								? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
								: "border-[var(--color-danger)]/40 bg-[var(--color-danger)]/15 text-[var(--color-danger)]"
						}`}
					>
						{idea.direction}
					</span>
					<span className="text-2xl font-semibold text-[var(--color-muted)]">
						{idea.allocationPct.toFixed(2)}%
					</span>
					<span className="ml-auto">
						<RoleBadge role={idea.proposerRole} />
					</span>
				</div>
			</header>

			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
				<h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-muted)]">
					Thesis
				</h2>
				<p className="text-sm leading-relaxed text-[var(--color-fg)]">
					{idea.thesis}
				</p>
				<dl className="grid grid-cols-2 gap-3 pt-3 text-xs text-[var(--color-muted)] sm:grid-cols-4">
					<div>
						<dt className="uppercase tracking-wider">Threshold</dt>
						<dd className="mt-1 text-sm text-[var(--color-fg)]">
							{(idea.thresholdPct * 100).toFixed(0)}%
						</dd>
					</div>
					<div>
						<dt className="uppercase tracking-wider">Quorum</dt>
						<dd className="mt-1 text-sm text-[var(--color-fg)]">
							{(idea.quorumPct * 100).toFixed(0)}%
						</dd>
					</div>
					<div>
						<dt className="uppercase tracking-wider">Window</dt>
						<dd className="mt-1 text-sm text-[var(--color-fg)]">
							{idea.voteWindowHours}h
						</dd>
					</div>
					<div>
						<dt className="uppercase tracking-wider">
							{votingDisabled ? "Status" : "Remaining"}
						</dt>
						<dd className="mt-1 text-sm text-[var(--color-fg)]">
							{windowRemaining ?? humanStatus(idea.status)}
						</dd>
					</div>
				</dl>
			</section>

			<section className="space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
				<div className="flex items-center justify-between">
					<h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-muted)]">
						Consensus
					</h2>
					<span className="text-xs text-[var(--color-muted)]">
						Updates every 5s
					</span>
				</div>
				<ConsensusBar consensus={consensus} thresholdPct={idea.thresholdPct} />
			</section>

			<section className="space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
				<h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-muted)]">
					Your vote
				</h2>
				<VoteButtons
					famSlug={slug}
					ideaId={id}
					disabled={votingDisabled}
					disabledReason={disabledReason}
				/>
			</section>

			{isProposer && !isResolved && (
				<section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
					<h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-muted)]">
						Proposer controls
					</h2>
					<p className="mt-2 text-xs text-[var(--color-muted)]">
						You proposed this idea. You can withdraw it any time before the
						window closes.
					</p>
					<button
						type="button"
						onClick={() => withdraw.mutate()}
						disabled={withdraw.isPending}
						className="mt-3 rounded-md border border-[var(--color-danger)]/50 px-3 py-1.5 text-sm text-[var(--color-danger)] transition hover:bg-[var(--color-danger)]/10 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{withdraw.isPending ? "Withdrawing…" : "Withdraw idea"}
					</button>
					{withdrawError && (
						<p className="mt-2 text-xs text-[var(--color-danger)]">
							{withdrawError}
						</p>
					)}
				</section>
			)}

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Simulated paper trade. Not investment advice. See{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					disclosures
				</a>
				.
			</footer>
		</div>
	);
}

function humanStatus(status: string): string {
	switch (status) {
		case "open":
			return "Open";
		case "passed":
			return "Passed";
		case "failed":
			return "Failed";
		case "quorum_failed":
			return "No quorum";
		case "pm_unilateral":
			return "PM entry";
		case "expired":
			return "Expired";
		case "withdrawn":
			return "Withdrawn";
		default:
			return status;
	}
}
